import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Divider, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1.5, fontSize: '1rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {title}
    </Typography>
    <Box sx={{ color: '#444', lineHeight: 1.8, fontSize: '0.95rem' }}>
      {children}
    </Box>
  </Box>
);

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f4f8', py: 6 }}>
      <Container maxWidth="md">

        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3, color: '#1E3A5F', textTransform: 'none', fontWeight: 600 }}
        >
          Back
        </Button>

        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <img src="/torch_logo.png" alt="Torch" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E3A5F' }}>
              Terms of Service
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Torch Beach Volleyball &nbsp;·&nbsp; Effective Date: March 13, 2026
          </Typography>
        </Box>

        {/* Card */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, sm: 5 }, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

          <Section title="Acceptance of Terms">
            <Typography>
              By accessing or using the Torch Beach VB application at <strong>app.torchvolleyball.com</strong> ("the App"),
              you agree to be bound by these Terms of Service. If you are registering on behalf of a minor athlete,
              you represent that you are the parent or legal guardian of that athlete and agree to these terms on their behalf.
              If you do not agree to these terms, please do not use the App.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Use of the App">
            <Typography paragraph>The App is provided exclusively for managing participation in Torch Beach Volleyball programs. Permitted uses include:</Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>Registering and managing athlete accounts.</li>
              <li style={{ marginTop: 8 }}>Purchasing session plans and tracking session balances.</li>
              <li style={{ marginTop: 8 }}>Viewing schedules, attendance, and program communications.</li>
              <li style={{ marginTop: 8 }}>Coaches managing class check-ins and athlete rosters.</li>
            </Box>
            <Typography sx={{ mt: 2 }}>
              You agree not to misuse the App, attempt to gain unauthorized access to other accounts, or use the App
              for any purpose other than its intended function.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Accounts & Access">
            <Typography paragraph>
              You are responsible for maintaining the confidentiality of your account credentials. You agree to notify
              Torch Beach Volleyball immediately if you suspect unauthorized use of your account.
            </Typography>
            <Typography>
              Access to the App is role-based. Admins, coaches, parents, and athletes each have access appropriate
              to their role. Attempting to access data or functionality outside your assigned role is prohibited.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Payments & Refunds">
            <Typography paragraph>
              Session plans are purchased through the App via Stripe, our third-party payment processor. By making a
              purchase you agree to Stripe's Terms of Service in addition to these terms.
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>All purchases are for session credits applied to your athlete's account.</li>
              <li style={{ marginTop: 8 }}>Session credits are non-transferable between athletes.</li>
              <li style={{ marginTop: 8 }}>Refunds are handled at the discretion of Torch Beach Volleyball staff and may be issued as credit or returned to the original payment method.</li>
              <li style={{ marginTop: 8 }}>Torch Beach Volleyball reserves the right to modify session plan pricing at any time.</li>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Program Participation">
            <Typography paragraph>
              Participation in Torch Beach Volleyball programs is subject to the following conditions:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>Athletes must maintain a positive session balance to participate in classes.</li>
              <li style={{ marginTop: 8 }}>Torch Beach Volleyball reserves the right to cancel or reschedule classes due to weather, court availability, or other circumstances beyond our control.</li>
              <li style={{ marginTop: 8 }}>Athletes are expected to conduct themselves respectfully toward coaches, staff, and other athletes. Disruptive behavior may result in removal from the program without refund.</li>
              <li style={{ marginTop: 8 }}>Junior athletes must have a parent or guardian account linked in the App.</li>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Communications">
            <Typography>
              By registering, you consent to receive transactional communications from Torch Beach Volleyball,
              including low-balance alerts, schedule updates, and program announcements via email or SMS.
              You may opt out of non-essential communications at any time by contacting us or updating your
              notification preferences in the App.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Intellectual Property">
            <Typography>
              All content within the App, including design, logos, text, and functionality, is the property of
              Torch Beach Volleyball. You may not reproduce, distribute, or create derivative works from any
              part of the App without express written permission.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Limitation of Liability">
            <Typography>
              Torch Beach Volleyball provides the App on an "as is" basis. We make no warranties regarding
              uptime, accuracy, or fitness for a particular purpose. To the fullest extent permitted by law,
              Torch Beach Volleyball shall not be liable for any indirect, incidental, or consequential damages
              arising from your use of the App.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Termination">
            <Typography>
              Torch Beach Volleyball reserves the right to suspend or terminate access to the App for any user
              who violates these Terms of Service, engages in fraudulent activity, or whose account has been
              inactive for an extended period. Upon termination, your right to use the App ceases immediately.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Changes to These Terms">
            <Typography>
              We may update these Terms of Service from time to time. We will notify users of significant changes
              via email or an in-app notice. Continued use of the App after changes are posted constitutes your
              acceptance of the revised terms.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Governing Law">
            <Typography>
              These Terms of Service are governed by the laws of the State of Florida, without regard to its
              conflict of law provisions. Any disputes arising from these terms shall be resolved in the courts
              of Florida.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Contact Us">
            <Typography>
              If you have questions about these Terms of Service, please contact us at:
            </Typography>
            <Box sx={{ mt: 2, p: 2.5, backgroundColor: '#f0f4f8', borderRadius: 2, borderLeft: '4px solid #C9A84C' }}>
              <Typography sx={{ fontWeight: 700, color: '#1E3A5F' }}>Torch Beach Volleyball</Typography>
              <Typography sx={{ color: '#555', mt: 0.5 }}>Email: <a href="mailto:info@torchvolleyball.com" style={{ color: '#1E3A5F' }}>info@torchvolleyball.com</a></Typography>
              <Typography sx={{ color: '#555' }}>Website: <a href="https://app.torchvolleyball.com" style={{ color: '#1E3A5F' }}>torchvolleyball.com</a></Typography>
            </Box>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center', mt: 2 }}>
            By using the Torch Beach VB app, you acknowledge that you have read, understood, and agree to these Terms of Service.
          </Typography>

        </Box>
      </Container>
    </Box>
  );
}

export default TermsOfService;
